
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Orbit, Store } from "lucide-react";

export default function Hero() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === "undefined" ? false : window.matchMedia("(min-width: 768px)").matches,
  );

  const videoSrc = isDesktop
    ? "/images/hero/home-desktop-web-v2.mp4"
    : "/images/hero/home-mobile-web-v2.mp4";
  const posterSrc = isDesktop
    ? "/images/hero/home-desktop-poster-v2.jpg"
    : "/images/hero/home-mobile-poster-v2.jpg";

  useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 768px)");
    const updateVideoSource = (event: MediaQueryListEvent) => setIsDesktop(event.matches);

    desktopQuery.addEventListener("change", updateVideoSource);

    return () => desktopQuery.removeEventListener("change", updateVideoSource);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.load();
    const playVideo = () => {
      if (!document.hidden) void video.play().catch(() => undefined);
    };

    playVideo();
    document.addEventListener("visibilitychange", playVideo);
    window.addEventListener("focus", playVideo);

    return () => {
      document.removeEventListener("visibilitychange", playVideo);
      window.removeEventListener("focus", playVideo);
    };
  }, [videoSrc]);

  return (
    <section className="relative h-[94vh] min-h-[700px] w-full overflow-hidden flex items-end">
      <picture className="absolute inset-0 block h-full w-full">
        <source
          media="(min-width: 768px)"
          srcSet="/images/hero/home-desktop-poster-v2.jpg"
        />
        <img
          src="/images/hero/home-mobile-poster-v2.jpg"
          alt="Four original masked guardians racing across floating worlds toward a radiant multiverse portal"
          className="h-full w-full object-cover"
          style={{ objectPosition: "center 35%" }}
          fetchPriority="high"
          decoding="async"
        />
      </picture>
      <video
        ref={videoRef}
        key={videoSrc}
        className="nm-hero-video pointer-events-none absolute inset-0 h-full w-full object-cover"
        poster={posterSrc}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        controls={false}
        disablePictureInPicture
        aria-hidden="true"
        tabIndex={-1}
        onCanPlay={(event) => void event.currentTarget.play().catch(() => undefined)}
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(4,16,47,.94) 0%, rgba(6,24,66,.72) 35%, rgba(4,12,35,.08) 68%), radial-gradient(80% 65% at 50% 100%, rgba(5,18,49,.88) 0%, transparent 65%)",
        }}
      />
      <div className="nm-grain absolute inset-0" />
      <motion.div
        initial={{ opacity: 0.6, scale: 1.08 }}
        animate={{ opacity: 0.35, scale: 1 }}
        transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(59,130,246,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.06) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(60% 60% at 50% 40%, black 0%, transparent 80%)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#061331] via-transparent to-[#061331]/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#061331] via-[#061331]/72 to-transparent md:hidden" />
      <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-[#061331] to-transparent" />

      <div className="nm-container relative w-full pb-10 md:pb-16">
        <motion.h1
          initial={{ opacity: 0, y: 26, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="nm-hero-title-3d"
        >
          <span className="nm-hero-title-line nm-hero-title-pearl" data-text="NERD">NERD</span>
          <span className="nm-hero-title-line nm-hero-title-silver" data-text="MULTIVERSE">MULTIVERSE</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 18, rotate: -1 }}
          animate={{ opacity: 1, y: 0, rotate: -1 }}
          transition={{ duration: 0.8, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="nm-hero-welcome"
        >
          <p>
            <span className="nm-hero-welcome-white">Welcome,</span>
            <span className="nm-hero-welcome-gold">fellow nerds,</span>
            <span className="nm-hero-welcome-white nm-hero-welcome-small">you’re finally home.</span>
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          className="mt-5 flex flex-wrap gap-3"
        >
          <Link
            to="/marketplace"
            className="nm-btn-primary gap-2"
          >
            EXPLORE THE MARKETPLACE <ArrowUpRight size={16} />
          </Link>
          <Link
            to="/sell"
            className="nm-btn-secondary"
          >
            SELL YOUR STUFF
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-5 hidden flex-wrap gap-x-6 gap-y-2 text-[11px] font-semibold tracking-wide text-white/72 sm:flex"
        >
          <span className="flex items-center gap-2"><Store size={15} className="text-[var(--nm-cyan)]" /> Collector-first marketplace</span>
          <span className="flex items-center gap-2"><Orbit size={15} className="text-violet-300" /> Curated across every fandom</span>
        </motion.div>
      </div>
    </section>
  );
}
